import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchPdfDocumentAction } from "../../../redux/actions/OrganizationAction/organizationDocumentAction";
import axios from "axios";
import { api } from "../../../api/api_route";
import { formatDistanceToNow } from "date-fns";
import {
  Card,
  CardContent,
  Typography,
  Button,
  Box,
  Tooltip,
  IconButton,
} from "@mui/material";
import { styled } from "@mui/system";
import { FiBell, FiClock, FiArrowRight } from "react-icons/fi";
import ReportGmailerrorredIcon from "@mui/icons-material/ReportGmailerrorred";
import LegalDocumentViewer from "./Loader";

const StyledCard = styled(Card)(({ theme }) => ({
  maxWidth: 400,
  margin: "20px auto",
  background: "linear-gradient(145deg, #ffffff 0%, #f5f5f5 100%)",
  borderRadius: 16,
  boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)",
  transition: "transform 0.3s ease-in-out",
  "&:hover": {
    transform: "translateY(-5px)",
  },
}));

const StyledButton = styled(Button)(({ theme }) => ({
  background: "linear-gradient(90deg, #6b48ff 0%, #8f74ff 100%)",
  color: "#ffffff",
  padding: "10px 24px",
  borderRadius: 25,
  textTransform: "none",
  fontWeight: 600,
  "&:hover": {
    background: "linear-gradient(90deg, #5a3aff 0%, #7e61ff 100%)",
  },
}));

const IconWrapper = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: 8,
  color: "#6b48ff",
});

const PdfViewer = () => {
  const dispatch = useDispatch();
  const { pdfData, loading, error, message } = useSelector(
    (state) => state?.organization
  );

  const [delayedLoading, setDelayedLoading] = useState(true);

  const currentPath = window.location.pathname;

  const documentUrl = api + currentPath;

  const [pdfObjectUrl, setPdfObjectUrl] = useState(null);
  const [pdfAvailable, setPdfAvailable] = useState(false);

  useEffect(() => {
    const checkPdfAvailability = async () => {
      try {
        await axios.head(documentUrl);
        setPdfAvailable(true);
      } catch (err) {
        console.error("PDF not available:", err);
        setPdfAvailable(false);
      }
    };

    if (documentUrl) {
      checkPdfAvailability();
    }
  }, [documentUrl]);

  useEffect(() => {
    if (pdfAvailable && documentUrl) {
      dispatch(fetchPdfDocumentAction(documentUrl));
    }
  }, [pdfAvailable, documentUrl, dispatch]);

  useEffect(() => {
    if (pdfData) {
      const blob = new Blob([pdfData], { type: "application/pdf" });
      const objectUrl = URL.createObjectURL(blob);
      setPdfObjectUrl(objectUrl);

      return () => {
        URL.revokeObjectURL(objectUrl);
      };
    }
  }, [pdfData]);

  useEffect(() => {
    if (loading || !loading) {
      const timer = setTimeout(() => {
        setDelayedLoading(false);
      }, 2000);

      return () => clearTimeout(timer);
    }
  }, [loading]);

  if (delayedLoading) {
    return <LegalDocumentViewer isLoading={true} />;
  }

  if (error) {
    return (
      <StyledCard role="alert" aria-live="polite">
        <CardContent>
          <IconWrapper>
            <ReportGmailerrorredIcon color="error" size={24} />
            <Typography
              variant="h5"
              component="h2"
              fontWeight={600}
              color="#2d2d2d"
            >
              {message ?? error.message}
            </Typography>
          </IconWrapper>
          <Typography variant="body1" sx={{ mt: 2, mb: 3, color: "#555555" }}>
            You do not have permission to view this document. It may be
            restricted or currently inaccessible. Please contact the person who
            shared the link or reach out to your organization administrator for
            assistance.
          </Typography>
        </CardContent>
      </StyledCard>
    );
  }

  if (!pdfAvailable) {
    return <LegalDocumentViewer isLoading={true} />;
  }

  return (
    <>
      {pdfObjectUrl && (
        <iframe
          //   src={pdfDoc}
          // src={`${pdfObjectUrl}#toolbar=0&navpanes=0`}
          src={`${pdfObjectUrl}`}
          width="100%"
          height="800"
          // style={{ border: "none" }}
          allowFullScreen
          title="PDF Flipbook"
        ></iframe>
      )}
    </>
  );
};

export default PdfViewer;

const TimeAgo = ({ timestamp }) => {
  const [timeAgo, setTimeAgo] = useState("");

  useEffect(() => {
    const update = () => {
      setTimeAgo(formatDistanceToNow(new Date(timestamp), { addSuffix: true }));
    };

    update();
    const interval = setInterval(update, 1000);

    return () => clearInterval(interval);
  }, [timestamp]);

  return (
    <Typography variant="caption" sx={{ ml: 1, color: "#666666" }}>
      {timeAgo}
    </Typography>
  );
};
